import React, { useRef, useState } from 'react';
import { Button, DatePicker, Input, Select } from 'antd';
import type { InputRef } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { renderFieldValue } from '../fields/renderFieldValue';
import { dateFieldValue, datePickerFormat } from '../lib/dateValue';
import { MarkdownEditorModal } from '../fields/MarkdownEditorModal';
import { spacing } from '../theme/tokens';
import type { FieldDefinition } from '@lens/shared';
import type { FieldLike, ReferenceOption } from './types';

/** Базовые декоративные размеры ячейки-просмотра (не влияют на onClick/onFocus/tabIndex/role). */
const CELL_MIN_HEIGHT = 22;
const CELL_PADDING = `0 ${spacing.xs}px`;

export interface CellEditorProps {
  value: unknown;
  field: FieldLike;
  onSave: (value: unknown) => void;
  resolvedRefs?: Record<string, string>;
  referenceOptions?: ReferenceOption[];
  /**
   * Загрузить опции для reference-поля при первом открытии редактора.
   * Возвращает массив { value, label }.
   */
  loadReferenceOptions?: (field: FieldLike) => Promise<ReferenceOption[]>;
  onResolvedRefsLoaded?: (refs: Record<string, string>) => void;
  itemId?: string;
  /**
   * Только для чтения: ячейка отображает значение, но не даёт войти в
   * режим редактирования (нет прав). По умолчанию false.
   */
  readOnly?: boolean;
}

/**
 * Inline-редактор ячейки. Принимает FieldLike (slug, name, fieldType, config),
 * не завязан на FieldDefinition. Сейв через onSave-колбэк.
 */
export function CellEditor({
  value,
  field,
  onSave,
  resolvedRefs,
  referenceOptions,
  loadReferenceOptions,
  onResolvedRefsLoaded,
  itemId,
  readOnly = false,
}: CellEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    value !== null && value !== undefined ? String(value) : '',
  );
  const inputRef = useRef<InputRef>(null);
  const [refOptions, setRefOptions] = useState<ReferenceOption[]>(referenceOptions ?? []);

  // FieldDefinition-совместимая обёртка для renderFieldValue
  const renderField = field as FieldDefinition;

  // computed-поля и режим readOnly (нет прав на редактирование) —
  // только отображение значения, без входа в редактор.
  if (field.fieldType === 'computed' || readOnly) {
    return (
      <div style={{ minHeight: CELL_MIN_HEIGHT, padding: CELL_PADDING }}>
        {renderFieldValue(value, renderField, resolvedRefs)}
      </div>
    );
  }

  const startEdit = () => {
    setDraft(value !== null && value !== undefined ? String(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const commit = () => {
    setEditing(false);
    let parsed: unknown = draft || null;
    if (field.fieldType === 'number') {
      const n = Number(draft);
      parsed = draft.trim() === '' ? null : Number.isNaN(n) ? null : n;
    }
    if (parsed !== value) {
      onSave(parsed);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (field.fieldType === 'reference' || field.fieldType === 'user') {
    const config = field.config as { dictionaryId?: string; multiple?: boolean } | null;
    const isMulti = config?.multiple ?? false;
    const options = refOptions.length > 0 ? refOptions : (referenceOptions ?? []);
    const renderReferenceFallback = () => {
      const labelById = new Map(options.map((option) => [option.value, option.label]));
      if (isMulti && Array.isArray(value)) {
        const labels = value.map(
          (entryId) =>
            labelById.get(String(entryId)) ?? resolvedRefs?.[String(entryId)] ?? String(entryId),
        );
        return labels.length > 0 ? labels.join(', ') : '—';
      }
      if (typeof value === 'string') {
        return labelById.get(value) ?? resolvedRefs?.[value] ?? value;
      }
      return null;
    };
    if (!editing) {
      return (
        <div
          style={{
            minHeight: CELL_MIN_HEIGHT,
            cursor: 'pointer',
            padding: CELL_PADDING,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          onClick={() => {
            setEditing(true);
            if (options.length === 0 && loadReferenceOptions) {
              loadReferenceOptions(field)
                .then((loaded) => {
                  setRefOptions(loaded);
                  onResolvedRefsLoaded?.(
                    Object.fromEntries(loaded.map((option) => [option.value, option.label])),
                  );
                })
                .catch(() => {});
            }
          }}
          tabIndex={0}
          role="button"
        >
          {renderReferenceFallback() ?? renderFieldValue(value, renderField, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        mode={isMulti ? 'multiple' : undefined}
        value={
          isMulti
            ? Array.isArray(value)
              ? value
              : []
            : typeof value === 'string'
              ? value
              : undefined
        }
        style={{ minWidth: 140 }}
        allowClear
        options={options}
        showSearch
        filterOption={(input, opt) =>
          (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        onChange={(v) => {
          const selectedIds = Array.isArray(v) ? v : typeof v === 'string' ? [v] : [];
          const nextResolvedRefs = Object.fromEntries(
            options
              .filter((option) => selectedIds.includes(option.value))
              .map((option) => [option.value, option.label]),
          );
          if (Object.keys(nextResolvedRefs).length > 0) {
            onResolvedRefsLoaded?.(nextResolvedRefs);
          }
          onSave(v ?? null);
          setEditing(false);
        }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
        placeholder="Выберите..."
      />
    );
  }

  if (field.fieldType === 'select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    const options = config?.options ?? [];
    if (!editing) {
      return (
        <div
          style={{ minHeight: CELL_MIN_HEIGHT, cursor: 'pointer', padding: CELL_PADDING }}
          onClick={startEdit}
          onFocus={startEdit}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, renderField, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        value={typeof value === 'string' ? value : undefined}
        style={{ minWidth: 120 }}
        allowClear
        options={options.map((o) => ({ value: o.value, label: o.label || o.value }))}
        onChange={(v) => {
          onSave(v ?? null);
          setEditing(false);
        }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  if (field.fieldType === 'multi_select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    const options = config?.options ?? [];
    if (!editing) {
      return (
        <div
          style={{ minHeight: CELL_MIN_HEIGHT, cursor: 'pointer', padding: CELL_PADDING }}
          onClick={startEdit}
          onFocus={startEdit}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, renderField, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        mode="multiple"
        value={Array.isArray(value) ? value : []}
        style={{ minWidth: 140 }}
        options={options.map((o) => ({ value: o.value, label: o.label || o.value }))}
        onChange={(v) => {
          onSave(v.length > 0 ? v : null);
          setEditing(false);
        }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  if (field.fieldType === 'boolean') {
    if (!editing) {
      return (
        <div
          style={{ minHeight: CELL_MIN_HEIGHT, cursor: 'pointer', padding: CELL_PADDING }}
          onClick={startEdit}
          onFocus={startEdit}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, renderField, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        value={typeof value === 'boolean' ? value : undefined}
        style={{ minWidth: 100 }}
        allowClear
        options={[
          { value: true, label: 'Да' },
          { value: false, label: 'Нет' },
        ]}
        onChange={(v) => {
          onSave(v ?? null);
          setEditing(false);
        }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  if (field.fieldType === 'date') {
    const config = field.config as { includeTime?: boolean } | null;
    if (!editing) {
      return (
        <div
          style={{ minHeight: CELL_MIN_HEIGHT, cursor: 'pointer', padding: CELL_PADDING }}
          onClick={startEdit}
          onFocus={startEdit}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, renderField, resolvedRefs)}
        </div>
      );
    }
    return (
      <DatePicker
        size="small"
        showTime={config?.includeTime ?? false}
        format={datePickerFormat(config?.includeTime ?? false)}
        value={value ? dayjs(String(value)) : null}
        onChange={(d) => {
          onSave(dateFieldValue(d, config?.includeTime ?? false));
          setEditing(false);
        }}
        // Не onBlur: клик по стрелкам месяца/года в панели календаря уводит
        // фокус с инпута, и blur закрывал бы редактор до переключения месяца
        onOpenChange={(open) => {
          if (!open) setEditing(false);
        }}
        autoFocus
        open
      />
    );
  }

  if (field.fieldType === 'markdown' || field.fieldType === 'textarea') {
    return (
      <>
        <div
          className="lens-mdcell"
          onClick={() => setEditing(true)}
          role="button"
          tabIndex={0}
          aria-label="Открыть поле в редакторе"
          title="Нажмите, чтобы открыть полный текст"
        >
          <div className="lens-mdcell__body">
            {renderFieldValue(value, renderField, resolvedRefs)}
          </div>
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            aria-label="Редактировать поле в большом окне"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            className="lens-mdcell__edit"
          />
        </div>
        <MarkdownEditorModal
          open={editing}
          value={value !== null && value !== undefined ? String(value) : ''}
          title={field.name}
          mode={field.fieldType === 'markdown' ? 'markdown' : 'textarea'}
          fieldName={field.name}
          itemId={itemId}
          onClose={() => setEditing(false)}
          onSave={(next) => {
            const parsed = next.trim() === '' ? null : next;
            if (parsed !== value) onSave(parsed);
          }}
        />
      </>
    );
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        size="small"
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        style={{ minWidth: 100 }}
      />
    );
  }

  return (
    <div
      style={{ minHeight: CELL_MIN_HEIGHT, cursor: 'text', padding: CELL_PADDING }}
      onClick={startEdit}
      onFocus={startEdit}
      tabIndex={0}
      role="button"
    >
      {renderFieldValue(value, renderField, resolvedRefs)}
    </div>
  );
}
